import {useCallback, useEffect, useState} from 'react'
import {View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {
  ALL_TEACHERS,
  type Classroom,
  type ClassroomApprovers,
  classroomApproversFor,
  type ClassroomMember,
  useClassroomActor,
} from '#/state/classrooms'
import {
  type ClassroomViewer,
  useClassroomMembersQuery,
  useSetModeratorMutation,
  useUpdateClassroomSettingsMutation,
} from '#/state/queries/classrooms'
import {useAgent, useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {type DialogControlProps} from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import * as Toggle from '#/components/forms/Toggle'
import {Lock_Stroke2_Corner0_Rounded as LockIcon} from '#/components/icons/Lock'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {showClassroomError} from './showClassroomError'

/**
 * How a classroom is run, as opposed to what it is.
 *
 * *Edit classroom* changes the words the room says about itself — its name, its
 * bio, the level it teaches. This changes who is at the door: who may say yes to
 * a student asking to join, who may say yes to a parent, and which of its
 * teachers the school has handed the room's own work to.
 *
 * The school is always at the door, and so is a moderator, whatever the two rows
 * say — they decide who *else* may answer (`canApproveJoinRequest`). Naming a
 * moderator is the one act here that is not a tap: it asks for the school's own
 * password, because handing away the door is how a room stops being one person's
 * to run.
 */
export function ClassroomSettingsDialog({
  control,
  classroom,
  viewer,
}: {
  control: DialogControlProps
  classroom: Classroom
  /** What the server says the reader may do in this room. */
  viewer: ClassroomViewer
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const actor = useClassroomActor()
  const agent = useAgent()
  const {currentAccount} = useSession()
  const updateSettings = useUpdateClassroomSettingsMutation(classroom.id)
  const setModerator = useSetModeratorMutation(classroom.id)

  /*
   * The roster is the server's, read on its own: the room view carries no
   * members, and who moderates is a flag on each member. Every page is read, so
   * no teacher is missing from the moderator candidates.
   */
  const {
    data: memberPages,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useClassroomMembersQuery(classroom.id)
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) void fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])
  const members = memberPages?.pages.flatMap(page => page.members) ?? []

  const canManage = viewer.canManage
  const isOwner = !!actor && actor.did === classroom.ownerDid
  const teachers = members.filter(m => m.role === 'teacher')

  const [studentApprovers, setStudentApprovers] = useState<ClassroomApprovers>(
    () => classroomApproversFor(classroom, 'student'),
  )
  const [parentApprovers, setParentApprovers] = useState<ClassroomApprovers>(
    () => classroomApproversFor(classroom, 'parent'),
  )

  /* The server seats a valid code at once unless the room says otherwise. */
  const savedAutoAccept = classroom.autoAcceptWithCode ?? true
  const [autoAccept, setAutoAccept] = useState(savedAutoAccept)

  /** The room is the truth: an answer that lands elsewhere is shown here. */
  useEffect(() => {
    setStudentApprovers(classroomApproversFor(classroom, 'student'))
    setParentApprovers(classroomApproversFor(classroom, 'parent'))
    setAutoAccept(classroom.autoAcceptWithCode ?? true)
  }, [classroom])

  /*
   * The two rows each have two answers and the room already holds one of them,
   * so *save* is only a real act once an answer has actually moved. It used to
   * sit above the moderators panel as the dialog's one big primary button — the
   * thing a reader assumes is the point of the screen — and do nothing at all
   * when nothing had changed.
   */
  const changed =
    !sameApprovers(
      studentApprovers,
      classroomApproversFor(classroom, 'student'),
    ) ||
    !sameApprovers(
      parentApprovers,
      classroomApproversFor(classroom, 'parent'),
    ) ||
    autoAccept !== savedAutoAccept

  const onSave = () => {
    if (!actor) return
    updateSettings.mutate(
      {studentApprovers, parentApprovers, autoAcceptWithCode: autoAccept},
      {
        onSuccess: () => Toast.show(l`Classroom settings saved`),
        onError: error => showClassroomError(i18n, error),
      },
    )
  }

  /*
   * Naming a moderator is asked for behind the school's password, so the ask is
   * state of its own: which teacher it is about, what was typed, and whether the
   * password was refused.
   */
  const [pending, setPending] = useState<ClassroomMember | null>(null)
  const [password, setPassword] = useState('')
  const [checking, setChecking] = useState(false)
  const [refused, setRefused] = useState(false)

  const onConfirmModerator = useCallback(async () => {
    if (!pending || !actor || !currentAccount) return
    setChecking(true)
    setRefused(false)
    try {
      /*
       * The password is checked by the account's own server rather than against
       * anything kept here: this device holds no password, and the only true
       * answer to *is this the school* is the one the sign-in itself gives.
       */
      await agent.com.atproto.server.createSession({
        identifier: currentAccount.handle,
        password,
      })
    } catch {
      setChecking(false)
      setRefused(true)
      return
    }
    /* The appointment is public once it is made; the password is not kept. */
    setPassword('')
    setChecking(false)
    setPending(null)
    const named = pending
    setModerator.mutate(
      {did: named.did, value: true},
      {
        onSuccess: () => Toast.show(l`${nameOf(named)} is now a moderator`),
        onError: error => showClassroomError(i18n, error),
      },
    )
  }, [pending, actor, currentAccount, agent, password, setModerator, i18n, l])

  const onRemoveModerator = (member: ClassroomMember) => {
    if (!actor) return
    setModerator.mutate(
      {did: member.did, value: false},
      {
        onSuccess: () =>
          Toast.show(l`${nameOf(member)} is no longer a moderator`),
        onError: error => showClassroomError(i18n, error),
      },
    )
  }

  const moderators = members.filter(m => m.isModerator)
  const candidates = teachers.filter(m => !m.isModerator)

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={l`Settings for ${classroom.name}`}>
        <View style={[a.gap_lg]}>
          <Text style={[a.text_xl, a.font_bold]}>{l`Classroom settings`}</Text>
          <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
            {l`Who answers the door of ${classroom.name}.`}
          </Text>

          <ApproverRow
            role="student"
            label={l`Students`}
            value={studentApprovers}
            onChange={setStudentApprovers}
            teachers={teachers}
            disabled={!canManage}
          />
          <ApproverRow
            role="parent"
            label={l`Parents`}
            value={parentApprovers}
            onChange={setParentApprovers}
            teachers={teachers}
            disabled={!canManage}
          />

          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {l`A teacher asking to join is for whoever runs the classroom to answer.`}
          </Text>

          <Toggle.Item
            label={l`Accept a valid code without asking`}
            name="autoAcceptWithCode"
            value={autoAccept}
            disabled={!canManage}
            onChange={setAutoAccept}
            style={[a.flex_row, a.align_center, a.justify_between, a.gap_md]}>
            <View style={[a.flex_1, a.gap_2xs]}>
              <Text style={[a.text_md, a.font_semi_bold, t.atoms.text]}>
                {l`Accept a valid code without asking`}
              </Text>
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {autoAccept
                  ? l`Anyone with the invitation code joins straight away.`
                  : l`People with the invitation code still wait for someone to say yes.`}
              </Text>
            </View>
            <Toggle.Switch />
          </Toggle.Item>

          <View
            style={[a.gap_sm, a.p_lg, a.rounded_md, t.atoms.bg_contrast_25]}>
            <Text style={[a.text_md, a.font_semi_bold]}>{l`Moderators`}</Text>
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              {l`A moderator answers requests, invites people and changes these rules. Only whoever runs the classroom can delete it.`}
            </Text>

            {moderators.length === 0 ? (
              <Text
                testID="classroomNoModerators"
                style={[a.text_sm, t.atoms.text_contrast_high]}>
                {l`No moderators yet.`}
              </Text>
            ) : (
              <View testID="classroomModerators" style={[a.gap_sm]}>
                {moderators.map(member => (
                  <View
                    key={member.did}
                    style={[a.flex_row, a.align_center, a.justify_between]}>
                    <Text
                      testID={`classroomModerator-${member.did}`}
                      style={[a.text_sm, a.font_semi_bold]}>
                      {nameOf(member)}
                    </Text>
                    {isOwner ? (
                      <Button
                        testID={`classroomModeratorRemove-${member.did}`}
                        label={l`Remove ${nameOf(member)} as a moderator`}
                        color="secondary"
                        size="small"
                        onPress={() => onRemoveModerator(member)}>
                        <ButtonText>{l`Remove`}</ButtonText>
                      </Button>
                    ) : null}
                  </View>
                ))}
              </View>
            )}

            {isOwner ? (
              candidates.length === 0 ? (
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  {l`Add a teacher to this classroom to share the work with them.`}
                </Text>
              ) : (
                <View style={[a.gap_sm]}>
                  <Text style={[a.text_sm, t.atoms.text_contrast_high]}>
                    {l`Make a teacher a moderator`}
                  </Text>
                  <View style={[a.flex_row, a.flex_wrap, a.gap_xs]}>
                    {candidates.map(member => (
                      <Button
                        key={member.did}
                        testID={`classroomModeratorAdd-${member.did}`}
                        label={l`Make ${nameOf(member)} a moderator`}
                        color="secondary"
                        size="small"
                        onPress={() => {
                          setPending(member)
                          setPassword('')
                          setRefused(false)
                        }}>
                        <ButtonText>{nameOf(member)}</ButtonText>
                      </Button>
                    ))}
                  </View>
                </View>
              )
            ) : null}

            {pending ? (
              <View style={[a.gap_sm]}>
                <Text style={[a.text_sm, t.atoms.text_contrast_high]}>
                  {l`Type your password to make ${nameOf(pending)} a moderator.`}
                </Text>
                <View>
                  <TextField.LabelText>{l`Your password`}</TextField.LabelText>
                  <TextField.Root>
                    <TextField.Icon icon={LockIcon} />
                    <TextField.Input
                      testID="classroomModeratorPassword"
                      label={l`Your password`}
                      autoCapitalize="none"
                      autoCorrect={false}
                      secureTextEntry
                      value={password}
                      onChangeText={setPassword}
                    />
                  </TextField.Root>
                </View>
                {refused ? (
                  <Text
                    testID="classroomModeratorPasswordError"
                    style={[a.text_sm, t.atoms.text_contrast_high]}>
                    {l`That password was not accepted.`}
                  </Text>
                ) : null}
                <View style={[a.flex_row, a.gap_sm]}>
                  <Button
                    testID="classroomModeratorConfirm"
                    label={l`Confirm`}
                    color="primary"
                    size="small"
                    disabled={checking || password.length === 0}
                    onPress={() => void onConfirmModerator()}>
                    <ButtonText>{l`Confirm`}</ButtonText>
                  </Button>
                  <Button
                    testID="classroomModeratorCancel"
                    label={l`Cancel`}
                    color="secondary"
                    size="small"
                    onPress={() => setPending(null)}>
                    <ButtonText>{l`Cancel`}</ButtonText>
                  </Button>
                </View>
              </View>
            ) : null}
          </View>

          {/*
            The save, under everything it saves.

            The moderators panel is a run of acts that happen at once — naming a
            teacher, removing one, all behind the password — and the button above
            it read as the way to commit *those*. It sits under them now, where
            the rules it commits are, and it is dark until one of them moves.
          */}
          <Button
            testID="classroomSettingsSave"
            label={l`Save classroom settings`}
            color="primary"
            size="large"
            disabled={!canManage || !changed || updateSettings.isPending}
            onPress={onSave}>
            <ButtonText>{l`Save`}</ButtonText>
          </Button>
        </View>
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/**
 * Whether two answers to a door are the same answer.
 *
 * `ALL_TEACHERS` is a value of its own and is compared as one; two lists are
 * compared as sets, because the same teachers written in another order are the
 * same rule and would otherwise look like a change.
 */
function sameApprovers(a: ClassroomApprovers, b: ClassroomApprovers): boolean {
  if (a === ALL_TEACHERS || b === ALL_TEACHERS) return a === b
  return a.length === b.length && a.every(did => b.includes(did))
}

/** The name a room calls somebody by, when it has no display name to use. */
function nameOf(member: ClassroomMember): string {
  return member.displayName || `@${member.handle}`
}

/**
 * One row of the door: every teacher, or only the ones the school names.
 *
 * Choosing *only some* starts with nobody listed, which is the honest reading of
 * a choice not made yet — the school then taps the teachers it means. The row is
 * drawn for moderators too but not disabled for them: changing these rules is
 * the work they were given.
 */
function ApproverRow({
  role,
  label,
  value,
  onChange,
  teachers,
  disabled,
}: {
  role: 'student' | 'parent'
  label: string
  value: ClassroomApprovers
  onChange: (next: ClassroomApprovers) => void
  teachers: ClassroomMember[]
  disabled: boolean
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const all = value === ALL_TEACHERS
  const chosen = all ? [] : value

  const toggle = (did: string) => {
    onChange(
      chosen.includes(did) ? chosen.filter(d => d !== did) : [...chosen, did],
    )
  }

  return (
    <View style={[a.gap_sm]}>
      <Text style={[a.text_md, a.font_semi_bold]}>{label}</Text>
      <View style={[a.flex_row, a.gap_sm]}>
        <Button
          testID={`classroomApproversAll-${role}`}
          label={l`All teachers answer ${label}`}
          color={all ? 'primary' : 'secondary'}
          size="small"
          disabled={disabled}
          onPress={() => onChange(ALL_TEACHERS)}>
          <ButtonText>{l`All teachers`}</ButtonText>
        </Button>
        <Button
          testID={`classroomApproversChosen-${role}`}
          label={l`Only some teachers answer ${label}`}
          color={all ? 'secondary' : 'primary'}
          size="small"
          disabled={disabled}
          onPress={() => onChange([])}>
          <ButtonText>{l`Only some`}</ButtonText>
        </Button>
      </View>
      {all ? null : teachers.length === 0 ? (
        <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {l`This classroom has no teachers in it yet.`}
        </Text>
      ) : (
        <View style={[a.flex_row, a.flex_wrap, a.gap_xs]}>
          {teachers.map(member => (
            <Button
              key={member.did}
              testID={`classroomApproverChip-${role}-${member.did}`}
              label={l`Let ${nameOf(member)} answer ${label}`}
              color={chosen.includes(member.did) ? 'primary' : 'secondary'}
              size="small"
              disabled={disabled}
              onPress={() => toggle(member.did)}>
              <ButtonText>{nameOf(member)}</ButtonText>
            </Button>
          ))}
        </View>
      )}
    </View>
  )
}
